'use client';
import { type PageTypes, usePage } from '@capsicum/common';
import { type PropsWithChildren } from 'react';
import styled, { css, type RuleSet, useTheme } from 'styled-components';

import { useWindowSize } from '../../hooks/helpers/useWindowSize';
import { type ItemBottomProps, NavRail } from '../molecules/nav-rail/NavRail';

type LayoutContainerProps = PropsWithChildren<{
	navRailItems?: Array<ItemBottomProps>;
}>;

export const LayoutContainer = ({ children, navRailItems }: LayoutContainerProps) => {
	const { pageType } = usePage();
	const { dimensions } = useWindowSize();
	const { breakpoints } = useTheme();
	const isAboveMobileBounds = dimensions.width > breakpoints.points.mobile.maxScreenWidth;
	const isPage = pageType === 'page' || pageType === 'page-with-sidePanel';

	return (
		<>
			{!isAboveMobileBounds && isPage && navRailItems && <NavRail defaultSelected="home" items={navRailItems} />}
			<S.AppContainer $variant={pageType} $withNavRail={!!navRailItems}>
				{isAboveMobileBounds && isPage && navRailItems && (
					<NavRail defaultSelected="home" items={navRailItems} />
				)}
				{children}
			</S.AppContainer>
		</>
	);
};

const AppContainerVariants: (
	withNavRail: boolean,
	variant: PageTypes
) => Partial<Record<PageTypes, RuleSet<object>>> = (withNavRail, variant) => ({
	'background-view': css`
		background-color: transparent;
		height: 100%;
	`,
	page: css`
		display: flex;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.screen};
		flex-direction: row;
		justify-content: center;
		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			align-items: center;
			flex-direction: column;
			justify-content: flex-start;
			min-height: calc(100vh - 64px);
			padding-bottom: ${() =>
				!variant.includes('subpage') && withNavRail ? `${NavRail.MobileBarHeight}px` : '0px'};

			@supports (min-height: calc(100svh - 64px)) {
				min-height: calc(100svh - 64px);
			}
		}
	`
});

namespace S {
	export const AppContainer = styled.div<{ $variant: PageTypes; $withNavRail: boolean }>`
		${({ $variant, $withNavRail }) =>
			AppContainerVariants($withNavRail, $variant)[$variant] ??
			AppContainerVariants($withNavRail, $variant)['page']}
	`;
}
